import { bool, func } from 'prop-types';
import React, { useState } from 'react';
import { Card, Col, Collapse, FormGroup, Input, Label, Row } from 'reactstrap';

import Association from '../../associations/Association';
import CurrencyInput from '../../generic/CurrencyInput';
import DateRange from '../../generic/DateRange';
import DiscardableForm from '../../generic/DiscardableForm';
import ExpandButton from '../../generic/ExpandButton';
import SingleSelect from '../../generic/SingleSelect';
import TimestampInfo from '../../generic/TimestampInfo';
import InfoTooltip from '../../generic/Tooltip/InfoTooltip';
import {
  businessModels,
  negotiatedByOptions,
} from '../../opportunities/constants';
import { agreementShape } from '../../prop-types';
import AgreementSalesUnit from '../AgreementSalesUnit';
import { agreementTypes, agreementTypeToBillingType } from '../constants';
import HELP_TEXTS from '../help-texts';
import AgreementBusinessSplit from './AgreementBusinessSplit';
import AgreementQuarterlySplit from './AgreementQuarterlySplit';

export default function AgreementForm({
  onSubmit,
  onDiscard,
  model,
  onChange,
  newModel,
}) {
  const inputProps = { onChange, readOnly: !onSubmit };
  const {
    name,
    accountId,
    ownerId,
    budget,
    type,
    created,
    createdBy,
    lastModified,
    lastModifiedBy,
    startDate,
    endDate,
    brandIds,
    businessModel,
    negotiatedBy,
    notes,
  } = model;

  const [hasExpandedSplits, setExpandedSplits] = useState(false);

  return (
    <DiscardableForm onSubmit={onSubmit} onDiscard={onDiscard}>
      <Row className="mb-5">
        <Col>
          <FormGroup row>
            <Label sm={3} for="agr-name" className="required">
              Name
            </Label>
            <Col sm={9}>
              <Input id="agr-name" name="name" value={name} {...inputProps} />
            </Col>
          </FormGroup>
          <FormGroup row className="align-items-center">
            <Label sm={3} for="agr-accountId" className="required">
              Account
            </Label>
            <Col sm={9}>
              <Association
                id="agr-accountId"
                name="accountId"
                value={accountId}
                type="account"
                showIcon
                {...inputProps}
              />
            </Col>
          </FormGroup>
          <AgreementSalesUnit agreement={model} {...inputProps} />
          <FormGroup row className="align-items-center">
            <Label sm={3} for="agr-ownerId" className="required">
              Owner
            </Label>
            <Col sm={9}>
              <Association
                type="user"
                id="agr-ownerId"
                name="ownerId"
                value={ownerId}
                {...inputProps}
              />
            </Col>
          </FormGroup>
          {!inputProps.readOnly && (
            <FormGroup row>
              <Label sm={3} for="agr-budget" className="required">
                Budget
              </Label>
              <Col sm={9}>
                <CurrencyInput
                  id="agr-budget"
                  name="budget"
                  value={budget}
                  {...inputProps}
                />
              </Col>
            </FormGroup>
          )}
          {!inputProps.readOnly && (
            <FormGroup row>
              <Label sm={3} for="agr-type" className="required">
                Type
              </Label>
              <Col sm={9}>
                <SingleSelect
                  options={agreementTypes}
                  id="agr-type"
                  name="type"
                  value={type}
                  {...inputProps}
                />
              </Col>
            </FormGroup>
          )}
          {inputProps.readOnly && (
            <FormGroup row>
              <Label sm={3} for="agr-billingType">
                Billing type
              </Label>
              <Col sm={9}>
                <span
                  className="form-control-plaintext"
                  data-qa-field="billingType"
                >
                  {agreementTypeToBillingType[type]}
                </span>
              </Col>
            </FormGroup>
          )}
          {!newModel && (
            <Row className="small">
              <Col>
                <TimestampInfo
                  timestamp={created}
                  actionName="created"
                  author={createdBy}
                  displayAttribute="inline"
                />
                <TimestampInfo
                  timestamp={lastModified}
                  actionName="lastModified"
                  author={lastModifiedBy}
                  displayAttribute="inline"
                />
              </Col>
            </Row>
          )}
        </Col>

        <Col>
          <FormGroup row>
            <Label sm={3} for="agr-timeFrame">
              Time frame
            </Label>
            <Col sm={9}>
              <DateRange
                id="agr-timeFrame"
                startName="startDate"
                endName="endDate"
                startDate={startDate}
                endDate={endDate}
                small
                {...inputProps}
              />
            </Col>
          </FormGroup>
          <FormGroup row>
            <Label sm={3} for="agr-brandIds">
              Brands
            </Label>
            <Col sm={9}>
              <Association
                id="agr-brandIds"
                name="brandIds"
                value={brandIds}
                type="brand"
                showIcon
                isMulti
                {...inputProps}
              />
            </Col>
          </FormGroup>
          <FormGroup row>
            <Label sm={3} for="agr-businessModel">
              Business Model
            </Label>
            <Col sm={9}>
              <SingleSelect
                options={businessModels}
                id="agr-businessModel"
                name="businessModel"
                value={businessModel}
                {...inputProps}
              />
            </Col>
          </FormGroup>
          <FormGroup row>
            <Label sm={3} for="agr-negotiatedBy" className="required">
              Negotiated by
            </Label>
            <Col sm={9}>
              <SingleSelect
                options={negotiatedByOptions}
                id="agr-negotiatedBy"
                name="negotiatedBy"
                value={negotiatedBy}
                {...inputProps}
              />
            </Col>
          </FormGroup>
          <FormGroup row>
            <Label sm={3} for="agr-notes">
              Notes
            </Label>
            <Col sm={9}>
              <Input
                type="textarea"
                id="agr-notes"
                name="notes"
                value={notes}
                {...inputProps}
              />
            </Col>
          </FormGroup>
        </Col>
      </Row>
      <Card body className="bg-light">
        <Row>
          <Col sm="12">
            <h4 className="d-flex">
              <ExpandButton
                className="mr-2"
                expanded={hasExpandedSplits}
                onClick={() => setExpandedSplits(!hasExpandedSplits)}
              />
              Quarterly and Business splits
              <span className="ml-1">
                <InfoTooltip target="budget-split-tooltip">
                  {HELP_TEXTS.tooltips.budgetSplit}
                </InfoTooltip>
              </span>
            </h4>
          </Col>
        </Row>
        <Collapse isOpen={hasExpandedSplits}>
          <Row className="mt-3">
            <Col sm="6">
              <AgreementQuarterlySplit model={model} {...inputProps} />
            </Col>
            <Col sm="6">
              <AgreementBusinessSplit model={model} {...inputProps} />
            </Col>
          </Row>
        </Collapse>
      </Card>
    </DiscardableForm>
  );
}

AgreementForm.propTypes = {
  model: agreementShape.isRequired,
  onChange: func,
  onSubmit: func,
  onDiscard: func,
  newModel: bool,
};

AgreementForm.defaultProps = {
  onChange: undefined,
  onSubmit: undefined,
  onDiscard: undefined,
  newModel: false,
};
